import React from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "@/components/landing/Wordmark";
import { ScreenplayPatternES } from "@/components/landing-es/ScreenplayPatternES";
import { es } from "@/locales/es";

// Social links are platform/brand names, not application copy — kept
// identical to the English footer (same hrefs, same labels).
const SOCIAL = [
  {
    name: "Instagram",
    href: "https://instagram.com/voxiumscript",
    ariaLabel: "Voxium Script en Instagram",
    testId: "footer-social-instagram-es",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61589900738949",
    ariaLabel: "Voxium Script en Facebook",
    testId: "footer-social-facebook-es",
  },
];

/**
 * Spanish footer. Legal links point to the EXISTING English legal pages
 * (/privacy, /terms, /refunds — no Spanish legal pages exist), labelled
 * in Spanish with a small "(en inglés)" suffix per link, plus a single
 * standalone disclaimer line beneath the legal nav (Alain's brief).
 */
export const SiteFooterES = () => {
  const t = es.footer;
  return (
    <footer
      className="relative w-full bg-ink overflow-hidden pt-14 sm:pt-20 pb-9 sm:pb-12"
      data-testid="site-footer-es"
    >
      <div className="absolute inset-0 pointer-events-none select-none">
        <ScreenplayPatternES className="w-full h-full" />
      </div>

      <div className="relative z-10 max-w-[78rem] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col items-center text-center gap-y-5 sm:gap-y-6">
          <Wordmark tone="dark" size="footer" testId="footer-wordmark-es" />

          <nav
            aria-label="Redes sociales de Voxium Script"
            className="font-ui text-creamtext text-base"
            data-testid="footer-social-nav-es"
          >
            <a
              href={SOCIAL[0].href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIAL[0].ariaLabel}
              className="text-creamtext hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid={SOCIAL[0].testId}
            >
              {SOCIAL[0].name}
            </a>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <a
              href={SOCIAL[1].href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIAL[1].ariaLabel}
              className="text-creamtext hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid={SOCIAL[1].testId}
            >
              {SOCIAL[1].name}
            </a>
          </nav>

          <nav
            aria-label="Aviso legal de Voxium Script"
            className="font-ui text-creamtext/85 text-sm"
            data-testid="footer-legal-nav-es"
          >
            <Link
              to="/privacy"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-privacy-link-es"
            >
              {t.legal.privacy}{" "}
              <span className="text-creamtext/50 text-xs">{t.legal.suffix}</span>
            </Link>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <Link
              to="/terms"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-terms-link-es"
            >
              {t.legal.terms}{" "}
              <span className="text-creamtext/50 text-xs">{t.legal.suffix}</span>
            </Link>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <Link
              to="/refunds"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-refunds-link-es"
            >
              {t.legal.refunds}{" "}
              <span className="text-creamtext/50 text-xs">{t.legal.suffix}</span>
            </Link>
          </nav>

          {/* Standalone legal-language disclaimer — explicit addition in
              Alain's brief, separate from the per-link "(en inglés)" tag. */}
          <p
            className="font-body text-xs text-creamtext/55 max-w-[46ch] leading-relaxed"
            data-testid="footer-legal-notice-es"
          >
            {t.legalNotice}
          </p>

          {/* DRAFT Spanish wording, pending native review — vox_ref
              cookie disclosure added per Alain's referral-capture brief
              (2026-10-21). Flagged for counsel re: Quebec Law 25. */}
          <p
            className="font-body text-xs text-creamtext/55 max-w-[46ch] leading-relaxed"
            data-testid="footer-referral-notice-es"
          >
            {t.referralNotice}
          </p>

          <div
            className="w-20 h-px bg-gold/40"
            aria-hidden="true"
            data-testid="footer-hairline-es"
          />

          <p
            className="font-ui text-[13px] text-creamtext/60 tracking-wide"
            data-testid="footer-copyright-es"
          >
            {t.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooterES;
